import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTareas } from '../../context/TareasContext';
import { usePlus } from '../../context/PlusContext';
import { useConfirmar } from '../../context/ConfirmarContext';
import {
  EnlaceCalendario, cambiarSoloMias, crearEnlaceCalendario, leerEnlaceCalendario, quitarEnlaceCalendario,
  renovarEnlaceCalendario, urlCalendario, urlGoogleCalendar, urlWebcal,
} from '../../utils/enlaceCalendarioTareas';
import {
  LISTA, activarRecordatorios, desactivarRecordatorios, esIOS, recordatoriosActivos, sincronizarRecordatorios,
} from '../../utils/recordatoriosTareas';
import { abrirEnlaceExterno, copiarTexto, esApp } from '../../utils/plataforma';
import { vibrar } from '../../utils/hapticos';
import { recordarAjusteDispositivo } from '../../utils/preferencias';
import { CalendarDays, Check, Copy, ListTodo, Sparkles } from 'lucide-react';
import { MensajeError, ModalBase, botonPrincipal, botonSecundario, chip } from './ui';

const abrir = (url: string) => (esApp || url.startsWith('http') ? abrirEnlaceExterno(url) : (window.location.href = url));

/**
 * Llevar las tareas fuera de myarinn: un calendario al que suscribirse
 * (Calendario de iOS, Google, Outlook) y, en el iPhone y el iPad, la lista de
 * Recordatorios. Las dos cosas son de Plus.
 */
export const ModalCalendarioTareas: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { user } = useAuth();
  const { tareas, casas, conPlusTareas, marcarHecha } = useTareas();
  const { ofrecerPlus } = usePlus();
  const confirmar = useConfirmar();
  const [enlace, setEnlace] = useState<EnlaceCalendario | null | undefined>(undefined);
  const [soloMias, setSoloMias] = useState(true);
  const [recordatorios, setRecordatorios] = useState<boolean | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) return;
    leerEnlaceCalendario(user.uid).then(e => { setEnlace(e); if (e) setSoloMias(e.soloMias); }).catch(() => setEnlace(null));
    if (esApp && esIOS) recordatoriosActivos().then(setRecordatorios);
  }, [user]);

  const hacer = async (fn: () => Promise<void>, fallo: string) => {
    setOcupado(true);
    setError('');
    try { await fn(); } catch (e) { console.error(e); setError(fallo); vibrar.error(); }
    setOcupado(false);
  };

  const crear = () => hacer(async () => { setEnlace(await crearEnlaceCalendario(user!.uid, soloMias)); vibrar.exito(); },
    'No se ha podido crear el enlace. Inténtalo de nuevo.');

  const elegir = (valor: boolean) => {
    setSoloMias(valor);
    if (enlace) hacer(async () => { await cambiarSoloMias(enlace.token, valor); setEnlace({ ...enlace, soloMias: valor }); },
      'No se ha podido cambiar.');
  };

  const renovar = async () => {
    if (!enlace || !(await confirmar({
      titulo: '¿Cambiar el enlace?',
      mensaje: 'El de ahora deja de funcionar: donde lo añadiste dejará de verse. Tendrás que añadir el nuevo.',
      accion: 'Cambiar',
    }))) return;
    hacer(async () => setEnlace(await renovarEnlaceCalendario(user!.uid, enlace)), 'No se ha podido cambiar el enlace.');
  };

  const quitar = async () => {
    if (!enlace || !(await confirmar({
      titulo: '¿Quitar el calendario?',
      mensaje: 'El enlace deja de funcionar. Quítalo también de tu Calendario para que no se quede vacío.',
      accion: 'Quitar', destructiva: true,
    }))) return;
    hacer(async () => { await quitarEnlaceCalendario(enlace.token); setEnlace(null); }, 'No se ha podido quitar.');
  };

  const copiar = async () => {
    if (!enlace) return;
    await copiarTexto(urlCalendario(enlace.token)).catch(() => {});
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  };

  const cambiarRecordatorios = () => hacer(async () => {
    if (recordatorios) {
      if (!(await confirmar({
        titulo: '¿Dejar de usar Recordatorios?',
        mensaje: `Se borra la lista «${LISTA}» de Recordatorios con lo que tenga.`,
        accion: 'Dejar de usar',
      }))) return;
      await desactivarRecordatorios();
      setRecordatorios(false);
      recordarAjusteDispositivo({ recordatoriosTareas: false });
      return;
    }
    if (await activarRecordatorios() === 'denegado') {
      setError('El iPhone no deja a myarinn usar Recordatorios. Actívalo en Ajustes → myarinn → Recordatorios.');
      return;
    }
    setRecordatorios(true);
    recordarAjusteDispositivo({ recordatoriosTareas: true });
    await sincronizarRecordatorios({ tareas, casas, uid: user?.uid, conPlus: conPlusTareas, marcarHecha });
    vibrar.exito();
  }, 'No se ha podido conectar con Recordatorios.');

  return (
    <ModalBase titulo="Calendario y Recordatorios" onClose={onClose}>
      <div className="p-5 space-y-5">
        <MensajeError texto={error} />

        {!conPlusTareas ? (
          <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
            <p className="text-sm text-amber-900 dark:text-amber-200">
              Ver tus tareas en el Calendario{esApp && esIOS ? ' y en Recordatorios' : ''} es de myarinn Plus. Si quien
              organiza tu casa tiene Plus, también lo tienes tú.
            </p>
            <button onClick={() => ofrecerPlus('tareasPlus')} className={`mt-3 ${botonPrincipal}`}>
              <Sparkles className="w-4 h-4" /> Ver myarinn Plus
            </button>
          </div>
        ) : (
          <>
            {/* ── Calendario suscrito ─────────────────────────────────────── */}
            <section className="space-y-3">
              <h3 className="flex items-center gap-2 font-bold text-gray-900 dark:text-white">
                <CalendarDays className="w-5 h-5 text-lumbre-600" /> En tu calendario
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Un enlace que añades una vez al Calendario del iPhone o del Mac, a Google Calendar o a Outlook. Se
                actualiza solo: lo que marcas en myarinn aparece allí al rato (en Google puede tardar unas horas).
              </p>
              <div className="flex flex-wrap gap-1.5">
                <button onClick={() => elegir(true)} disabled={ocupado} className={chip(soloMias)}>Las mías</button>
                <button onClick={() => elegir(false)} disabled={ocupado} className={chip(!soloMias)}>Todas las de mis casas</button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {soloMias ? 'Las que te tocan a ti, las de cualquiera y las de «Mis tareas».' : 'Todo lo de tus casas, con quién lo hace.'}
              </p>

              {enlace === undefined ? (
                <p className="text-sm text-gray-500">Cargando…</p>
              ) : !enlace ? (
                <button onClick={crear} disabled={ocupado} className={`w-full py-2.5 ${botonPrincipal}`}>
                  {ocupado ? 'Creando…' : 'Crear mi enlace de calendario'}
                </button>
              ) : (
                <>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <button onClick={() => abrir(urlWebcal(enlace.token))} className={`py-2.5 ${botonPrincipal}`}>
                      Añadir al Calendario
                    </button>
                    <button onClick={() => abrir(urlGoogleCalendar(enlace.token))} className={`py-2.5 ${botonSecundario}`}>
                      Añadir a Google Calendar
                    </button>
                  </div>
                  <button onClick={copiar} className={`w-full py-2 ${botonSecundario}`}>
                    {copiado ? <><Check className="w-4 h-4" /> Copiado</> : <><Copy className="w-4 h-4" /> Copiar el enlace (Outlook y otros)</>}
                  </button>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Quien tenga el enlace puede ver estas tareas. Si lo has compartido sin querer,{' '}
                    <button onClick={renovar} disabled={ocupado} className="font-semibold text-lumbre-700 dark:text-lumbre-400 hover:underline">
                      cámbialo por otro
                    </button>{' '}o{' '}
                    <button onClick={quitar} disabled={ocupado} className="font-semibold text-red-600 dark:text-red-400 hover:underline">
                      quítalo
                    </button>.
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    ¿No se añade en el iPhone? Copia el enlace y ve a Ajustes → Calendario → Cuentas → Añadir cuenta →
                    Otra → Añadir calendario suscrito, y pégalo allí.
                  </p>
                </>
              )}
            </section>

            {/* ── Recordatorios de iOS ────────────────────────────────────── */}
            {esApp && esIOS && recordatorios !== null && (
              <section className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="flex items-center gap-2 font-bold text-gray-900 dark:text-white">
                      <ListTodo className="w-5 h-5 text-lumbre-600" /> En Recordatorios
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Las tareas que te tocan, en una lista «{LISTA}». Si tachas una allí, al abrir myarinn se marca
                      hecha para toda la casa y aparece la siguiente.
                    </p>
                  </div>
                  <button role="switch" aria-checked={recordatorios} aria-label="Usar Recordatorios" onClick={cambiarRecordatorios}
                    disabled={ocupado}
                    className={`relative w-12 h-7 rounded-full transition shrink-0 disabled:opacity-50 ${
                      recordatorios ? 'bg-lumbre-600' : 'bg-gray-300 dark:bg-gray-600'}`}>
                    <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition ${recordatorios ? 'translate-x-5' : ''}`} />
                  </button>
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </ModalBase>
  );
};
